import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Heart, Calendar, Stars, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'values', icon: Heart, accentClass: 'text-rose-600 dark:text-rose-400' },
  { key: 'annual_themes', icon: Calendar, accentClass: 'text-amber-600 dark:text-amber-400' },
  { key: 'dreams', icon: Stars, accentClass: 'text-indigo-600 dark:text-indigo-400' }
];

/**
 * Family vision board. Three columns: values, annual themes, dreams.
 * Replaces the corporate cascading-goals view when workspace.type === 'family'.
 *
 * Props:
 *   workspace: { id, name, description, type }
 *   token: JWT
 */
export default function FamilyVisionBoard({ workspace, token }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // addingTo: 'values' | 'annual_themes' | 'dreams' | null
  const [addingTo, setAddingTo] = useState(null);
  const [form, setForm] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchItems = useCallback(async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workspaces/${workspace.id}/vision-board`, axiosConfig);
      setItems(data);
    } catch (error) {
      toast.error(t('family.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [workspace, axiosConfig, t]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const itemsByCategory = useMemo(() => {
    const grouped = { values: [], annual_themes: [], dreams: [] };
    for (const it of items) {
      const cat = grouped[it.category] ? it.category : 'values';
      grouped[cat].push(it);
    }
    return grouped;
  }, [items]);

  const openAdd = (category) => {
    setAddingTo(category);
    setForm({ title: '', content: '' });
  };

  const createItem = async (e) => {
    e.preventDefault();
    if (!addingTo || !form.title.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/vision-board`,
        {
          workspace_id: workspace.id,
          title: form.title,
          content: form.content,
          category: addingTo
        },
        axiosConfig
      );
      toast.success(t('family.created'));
      setAddingTo(null);
      setForm({ title: '', content: '' });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('family.createError'));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm(t('family.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/vision-board/${id}`, axiosConfig);
      toast.success(t('family.deleted'));
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('family.deleteError'));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">{workspace?.name}</h2>
        {workspace?.description && (
          <p className="text-sm text-muted-foreground mt-1 font-serif italic">{workspace.description}</p>
        )}
        <p className="text-xs text-muted-foreground uppercase tracking-[0.18em] mt-3">{t('family.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          {t('family.loading')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {CATEGORIES.map((cat) => (
            <CategoryColumn
              key={cat.key}
              category={cat}
              items={itemsByCategory[cat.key] || []}
              onAdd={() => openAdd(cat.key)}
              onDelete={deleteItem}
              addingHere={addingTo === cat.key}
              form={form}
              setForm={setForm}
              onSubmit={createItem}
              onCancel={() => setAddingTo(null)}
              saving={saving}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryColumn({ category, items, onAdd, onDelete, addingHere, form, setForm, onSubmit, onCancel, saving, t }) {
  const Icon = category.icon;
  return (
    <section className="space-y-3" data-testid={`vision-column-${category.key}`}>
      <header className="flex items-center justify-between pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${category.accentClass}`} />
          <h3 className="font-serif text-base sm:text-lg font-light tracking-tight">{t(`family.cat.${category.key}.title`)}</h3>
        </div>
        <Button
          onClick={onAdd}
          variant="ghost"
          size="sm"
          className="rounded-full h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          data-testid={`add-vision-${category.key}-btn`}
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </header>

      <p className="text-xs text-muted-foreground italic font-serif leading-relaxed">
        {t(`family.cat.${category.key}.tagline`)}
      </p>

      {addingHere && (
        <form
          onSubmit={onSubmit}
          className="p-3 rounded-lg border border-accent/30 bg-accent/5 space-y-2"
          data-testid={`vision-form-${category.key}`}
        >
          <Input
            placeholder={t(`family.cat.${category.key}.titlePlaceholder`)}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            autoFocus
            className="text-sm"
            data-testid={`vision-title-input-${category.key}`}
          />
          <Textarea
            placeholder={t(`family.cat.${category.key}.contentPlaceholder`)}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={3}
            className="resize-none text-xs sm:text-sm"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving || !form.title.trim()} className="rounded-full text-xs">
              {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : null}
              {t('form.create')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={saving} className="text-xs">
              {t('form.cancel')}
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-2.5">
        {items.length === 0 && !addingHere ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-xs font-serif italic">{t(`family.cat.${category.key}.empty`)}</p>
          </div>
        ) : (
          items.map((it) => (
            <article
              key={it.id}
              className="p-3 rounded-lg border border-border bg-card/40 group fade-in"
              data-testid={`vision-item-${it.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="font-medium text-sm leading-snug flex-1">{it.title}</h4>
                <button
                  type="button"
                  onClick={() => onDelete(it.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  data-testid={`delete-vision-${it.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {it.content && (
                <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">{it.content}</p>
              )}
            </article>
          ))
        )}
      </div>
    </section>
  );
}
